import { CalendarBlank, ListChecks } from "@phosphor-icons/react"
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router"
import {
  CreateOptionType,
  ManageCollectionDialog,
} from "~/components/collections/dialogs/ManageCollectionDialog"
import { Module, OverviewPage } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Button } from "~/components/reusable/Button"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { CollectionProvider } from "~/context/CollectionProvider"
import { useGetCollectionsWithDetails, useCollectionGroups } from "~/hooks/useCollections"
import { usePageTracking } from "~/hooks/usePageTracking"

export const Route = createFileRoute("/_auth/demand-planning/fashion/_layout")({
  component: RouteComponent,
})

const CollectionTab = {
  timeline: "Timeline",
  tasks: "Tasks",
} as const

const tabs: HeaderTab[] = [
  {
    label: CollectionTab.timeline,
    path: "/demand-planning/fashion/timeline",
    icon: <CalendarBlank />,
  },
  { label: CollectionTab.tasks, path: "/demand-planning/fashion/tasks", icon: <ListChecks /> },
]

function RouteComponent() {
  const router = useRouter()
  const { data: rawCollections, isLoading: detailsLoading } = useGetCollectionsWithDetails()
  const { data: collectionGroups, isLoading: isCollectionGroupsLoading } = useCollectionGroups()
  usePageTracking("Collections")

  if (detailsLoading || isCollectionGroupsLoading) return <LoadingOverlay />

  // Filter out NOOS collections - only show fashion/mixed collections
  const fashionCollections = rawCollections?.filter((c) => c.collectionType !== "noos") ?? []
  const hasFashion = fashionCollections.length > 0

  return (
    <Module
      breadcrumb={<BreadcrumbWrapper />}
      actions={
        <Button
          variant="neutral"
          size="sm"
          onClick={() => router.navigate({ to: "/collection-management" })}
        >
          Manage Collections
        </Button>
      }
      tabs={hasFashion ? { items: tabs, path: "/demand-planning/fashion" } : undefined}
    >
      {hasFashion ? (
        <CollectionProvider value={{ collections: fashionCollections, collectionGroups }}>
          <Outlet />
        </CollectionProvider>
      ) : (
        <OverviewPage fillContent>
          <div className="flex-1 flex flex-col gap-2 items-center justify-center">
            <img src="/no_collections.png" alt="No collection" className="w-[220px] h-[160px]" />
            <p className="heading-sm">Collections</p>
            <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
              No collections yet! Create your first collection to get started.{" "}
            </p>
            <ManageCollectionDialog defaultCollectionType={CreateOptionType.Fashion} />
          </div>
        </OverviewPage>
      )}
    </Module>
  )
}
